/**
 * RANGOS DE COLECCIONISTA (screen #27 `rangos_de_coleccionista`): barra de XP +
 * jerarquía de los 6 rangos (desbloqueado/actual/bloqueado) + beneficios del
 * rango actual + reclamar recompensas.
 *
 * Todo se DERIVA del ledger `xp_event` [L] vía `useGamification` (reducer puro
 * `rankProgress`). Traducción del diseño Stitch a SolidJS + UI-vars + lucide.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { CheckCircle2, Gift, Leaf, Lock, Sparkles } from "lucide-solid";
import { RANKS } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useGamification } from "../hooks/use-gamification";
import { rankIcon, rankName, rankTagline, rewardIcon, rewardLabel } from "../rank-presentation";
import { RankUpModal } from "./rank-up-modal";

export function RanksPage() {
  const { t } = useI18n();
  const gam = useGamification();
  const [claimOpen, setClaimOpen] = createSignal(false);

  const current = createMemo(() => gam.progress().rank);
  const percentInRank = createMemo(() => Math.round(gam.progress().ratio * 100));
  const unlockedCount = createMemo(() => gam.ranks().filter((r) => r.unlocked).length);

  const xpLabel = () => {
    const p = gam.progress();
    if (p.isMax) {
      const xp = p.totalXp;
      return t(msg`${xp} XP · rango máximo`);
    }
    const into = p.intoRank;
    const span = p.rankSpan;
    return t(msg`${into} / ${span} XP`);
  };

  return (
    <div class="flex flex-col gap-8" data-testid="ranks-page">
      {/* Hero: rango actual — tarjeta forest rellena (firma visual Stitch). */}
      <section class="relative overflow-hidden rounded-2xl bg-primary p-6 text-primary-foreground shadow-sm">
        <div class="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
          <div class="relative grid h-28 w-28 shrink-0 place-items-center rounded-full border-4 border-primary-foreground/70 bg-primary-foreground">
            {(() => {
              const Icon = rankIcon(current().icon);
              return <Icon class="h-14 w-14 text-primary" />;
            })()}
            <span
              class="absolute -right-2 -top-2 rounded-full bg-danger px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-danger-foreground"
              data-testid="ranks-percentile"
            >
              {gam.percentile()}%
            </span>
          </div>
          <div class="flex-1 text-center sm:text-left">
            <p class="text-xs font-bold uppercase tracking-widest text-primary-foreground/70">
              {t(msg`Tu rango actual`)}
            </p>
            <h1
              class="mt-1 text-2xl font-semibold text-primary-foreground"
              data-testid="ranks-current-name"
            >
              {rankName(t, current().id)}
            </h1>
            <p class="mt-1 text-sm italic text-primary-foreground/80">
              {rankTagline(t, current().id)}
            </p>

            {/* Barra de XP */}
            <div class="mt-4 space-y-1.5">
              <div class="flex items-end justify-between text-xs font-semibold text-primary-foreground">
                <span data-testid="ranks-xp">{xpLabel()}</span>
                <Show when={!gam.progress().isMax}>
                  <span>{percentInRank()}%</span>
                </Show>
              </div>
              <div class="h-3 w-full overflow-hidden rounded-full bg-primary-foreground/20">
                <div
                  class="h-full rounded-full bg-primary-foreground transition-[width] duration-700"
                  style={{ width: `${gam.progress().isMax ? 100 : percentInRank()}%` }}
                />
              </div>
              <Show when={gam.progress().nextRank}>
                {(next) => (
                  <p class="text-right text-xs text-primary-foreground/70">
                    {t(msg`Siguiente:`)} {rankName(t, next().id)}
                  </p>
                )}
              </Show>
            </div>
          </div>
        </div>
      </section>

      <div class="grid gap-6 lg:grid-cols-12">
        {/* Beneficios del rango actual */}
        <aside class="lg:col-span-5">
          <div class="rounded-2xl border border-border/60 bg-card/70 p-6">
            <h2 class="mb-5 flex items-center gap-2 text-lg font-semibold text-primary">
              <Sparkles class="h-5 w-5" />
              {t(msg`Beneficios del rango`)}
            </h2>
            <Show
              when={current().rewards.length > 0}
              fallback={
                <p class="text-sm text-muted-foreground">
                  {t(msg`Aún no hay beneficios en este rango. ¡Sigue coleccionando!`)}
                </p>
              }
            >
              <ul class="space-y-4">
                <For each={current().rewards}>
                  {(reward) => {
                    const RewardIcon = rewardIcon(reward.icon);
                    return (
                      <li class="flex gap-3">
                        <span class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
                          <RewardIcon class="h-5 w-5" />
                        </span>
                        <p class="text-sm text-foreground">{rewardLabel(t, reward.key)}</p>
                      </li>
                    );
                  }}
                </For>
              </ul>
            </Show>
            <button
              type="button"
              data-testid="ranks-claim-button"
              class="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-semibold text-primary-foreground transition-transform active:scale-[0.98]"
              onClick={() => setClaimOpen(true)}
            >
              <Gift class="h-5 w-5" />
              {t(msg`Reclamar recompensas`)}
            </button>
          </div>
        </aside>

        {/* Jerarquía de rangos */}
        <section class="lg:col-span-7">
          <div class="rounded-2xl border border-border/60 bg-card/70 p-6">
            <div class="mb-6 flex items-center justify-between">
              <h2 class="text-lg font-semibold text-foreground">
                {t(msg`Progreso del caminante`)}
              </h2>
              <span class="rounded-full bg-accent px-3 py-1 text-xs font-bold text-accent-foreground">
                {unlockedCount()} / {RANKS.length}
              </span>
            </div>
            <ol class="relative space-y-6 border-l-2 border-dashed border-border/70 pl-8">
              <For each={gam.ranks()}>
                {(status) => {
                  const Icon = rankIcon(status.rank.icon);
                  const xpRemaining = status.xpRemaining;
                  return (
                    <li class="relative" data-testid="rank-row" data-unlocked={status.unlocked}>
                      <span
                        class="absolute -left-[41px] grid h-10 w-10 place-items-center rounded-full border-4 border-background"
                        classList={{
                          "bg-primary text-primary-foreground": status.unlocked,
                          "bg-muted text-muted-foreground": !status.unlocked,
                          "ring-2 ring-primary ring-offset-2 ring-offset-background":
                            status.current,
                        }}
                      >
                        <Icon class="h-5 w-5" />
                      </span>
                      <div
                        class="flex items-center justify-between gap-3 rounded-xl px-3 py-2"
                        classList={{ "bg-primary/5": status.current }}
                      >
                        <div>
                          <div class="flex items-center gap-2">
                            <h3
                              class="text-sm font-semibold"
                              classList={{
                                "text-foreground": status.unlocked,
                                "text-muted-foreground": !status.unlocked,
                              }}
                            >
                              {rankName(t, status.rank.id)}
                            </h3>
                            <Show when={status.current}>
                              <span class="rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-tight text-primary-foreground">
                                {t(msg`Actual`)}
                              </span>
                            </Show>
                          </div>
                          <p class="mt-0.5 text-xs text-muted-foreground">
                            {rankTagline(t, status.rank.id)}
                          </p>
                          <Show when={!status.unlocked && status.xpRemaining > 0}>
                            <p class="mt-1 text-[10px] font-bold uppercase tracking-wide text-danger">
                              {t(msg`Faltan ${xpRemaining} XP`)}
                            </p>
                          </Show>
                        </div>
                        <Show
                          when={status.unlocked}
                          fallback={<Lock class="h-5 w-5 shrink-0 text-muted-foreground/50" />}
                        >
                          <Show
                            when={status.current}
                            fallback={<CheckCircle2 class="h-5 w-5 shrink-0 text-primary" />}
                          >
                            <Leaf class="h-5 w-5 shrink-0 text-primary" />
                          </Show>
                        </Show>
                      </div>
                    </li>
                  );
                }}
              </For>
            </ol>
          </div>
        </section>
      </div>

      <RankUpModal open={claimOpen()} onOpenChange={setClaimOpen} rank={current()} mode="claim" />
    </div>
  );
}
